import { Star } from "lucide-react";
import { Label } from "./ui/label";

export const BTN = "bg-[#E08A1E] hover:bg-[#C87515] text-white rounded-full";

export const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}><Label>{label}</Label>{children}</div>
);

export const PageHeader = ({ eyebrow, title, subtitle, actions }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 fade-up">
    <div>
      {eyebrow && <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#E08A1E] mb-2">{eyebrow}</div>}
      <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#1D1D1F]" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-[#6E6E73] mt-2 max-w-xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const StatCard = ({ label, value, icon: Icon, hint, testid, accent }) => (
  <div className={`rounded-2xl p-5 border transition-transform duration-200 hover:-translate-y-0.5 ${accent ? "bg-[#0A0807] text-white border-[#2C2A29]" : "bg-white border-[#E5E5EA]"}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className={`text-[11px] font-bold uppercase tracking-wider ${accent ? "text-neutral-400" : "text-[#6E6E73]"}`}>{label}</span>
      {Icon && <Icon className="h-4 w-4 text-[#E08A1E]" />}
    </div>
    <div className="text-3xl font-extrabold tracking-tight mt-3" data-testid={testid ? `${testid}-value` : undefined}>{value}</div>
    {hint && <div className={`text-xs mt-1 ${accent ? "text-neutral-400" : "text-[#6E6E73]"}`}>{hint}</div>}
  </div>
);

const BADGE = {
  present: "bg-emerald-50 text-emerald-700", approved: "bg-emerald-50 text-emerald-700", completed: "bg-emerald-50 text-emerald-700",
  active: "bg-emerald-50 text-emerald-700", open: "bg-emerald-50 text-emerald-700", hired: "bg-emerald-50 text-emerald-700",
  late: "bg-amber-50 text-amber-700", pending: "bg-amber-50 text-amber-700", pending_self: "bg-amber-50 text-amber-700", pending_hr: "bg-amber-50 text-amber-700", half_day: "bg-amber-50 text-amber-700",
  pending_manager: "bg-blue-50 text-blue-700", absent: "bg-red-50 text-red-700", rejected: "bg-red-50 text-red-700",
  inactive: "bg-neutral-100 text-neutral-500", closed: "bg-neutral-100 text-neutral-500", on_leave: "bg-blue-50 text-blue-700", holiday: "bg-purple-50 text-purple-700", week_off: "bg-neutral-100 text-neutral-600",
};

export const StatusBadge = ({ status, testid }) => (
  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize whitespace-nowrap ${BADGE[status] || "bg-[#FDF6ED] text-[#C87515]"}`} data-testid={testid}>
    {(status || "").replace(/_/g, " ")}
  </span>
);

export const EmptyState = ({ text, testid }) => (
  <div className="py-14 text-center text-sm text-[#6E6E73] border border-dashed border-[#E5E5EA] rounded-2xl bg-white" data-testid={testid}>{text}</div>
);

export const StarRating = ({ value, onChange, testid }) => (
  <div className="flex gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)} data-testid={`${testid}-star-${n}`}
        className="transition-transform hover:scale-110 disabled:cursor-default disabled:hover:scale-100">
        <Star className={`h-5 w-5 ${n <= (value || 0) ? "fill-[#E08A1E] text-[#E08A1E]" : "text-neutral-300"}`} />
      </button>
    ))}
  </div>
);

export const Panel = ({ title, children, className = "", actions, testid }) => (
  <div className={`bg-white border border-[#E5E5EA] rounded-2xl p-5 md:p-6 ${className}`} data-testid={testid}>
    {(title || actions) && (
      <div className="flex items-center justify-between mb-4 gap-2">
        <h3 className="text-base font-bold text-[#1D1D1F]">{title}</h3>
        {actions}
      </div>
    )}
    {children}
  </div>
);
